import { memo, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';

function usePulseOpacity() {
  const [opacity, setOpacity] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const t = (Date.now() - start) / 1000;
      setOpacity((Math.sin(2 * Math.PI * 0.375 * t) + 1) / 8);
    }, 50);
    return () => clearInterval(id);
  }, []);
  return opacity;
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Draggable point that maps pixel position back to data-space coords.
 * @param {{ x, y, axis, color, size, yIsDown, showRegion, onChange }} props
 */
function IntFigMovablePoint({ x: dataX, y: dataY, axis, color = '#1f76D2', size = 1.0, yIsDown = true, showRegion = false, onChange }) {
  const containerRef = useRef(null);
  const thumbRef = useRef(null);
  const [parentSize, setParentSize] = useState({ width: 0, height: 0 });
  const pulseOpacity = usePulseOpacity();

  useEffect(() => {
    const el = containerRef.current?.offsetParent;
    if (!el) return;
    const update = () => setParentSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const diameter = (size / 0.7) * 15 * Math.max(parentSize.width, parentSize.height) / 215;
  const travel_x = Math.max(parentSize.width - diameter, 1);
  const travel_y = Math.max(parentSize.height - diameter, 1);

  const px = ((dataX - axis.min_x) / (axis.max_x - axis.min_x)) * travel_x;
  const py = yIsDown
    ? ((dataY - axis.min_y) / (axis.max_y - axis.min_y)) * travel_y
    : travel_y - ((dataY - axis.min_y) / (axis.max_y - axis.min_y)) * travel_y;

  const bounds = {
    left: 0, right: travel_x,
    top: 0, bottom: travel_y,
  };

  const handleDrag = (_, pos) => {
    let newX = (pos.x / travel_x) * (axis.max_x - axis.min_x) + axis.min_x;
    let newY = yIsDown
      ? (pos.y / travel_y) * (axis.max_y - axis.min_y) + axis.min_y
      : ((travel_y - pos.y) / travel_y) * (axis.max_y - axis.min_y) + axis.min_y;
    newX = Math.max(axis.min_x, Math.min(axis.max_x, newX));
    newY = Math.max(axis.min_y, Math.min(axis.max_y, newY));
    onChange(newX, newY);
  };

  if (parentSize.width === 0 && parentSize.height === 0) {
    return <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />;
  }

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0 }}>
      <Draggable
        nodeRef={thumbRef}
        position={{ x: px, y: py }}
        bounds={bounds}
        onDrag={handleDrag}
      >
        <div
          ref={thumbRef}
          className="ct__intfig-movpt-thumb"
          style={{ width: diameter, height: diameter }}
        >
          <svg width="100%" height="100%" viewBox="-0.5 -0.5 1 1">
            <circle cx={0} cy={0} r={0.5} fill={hexToRgba(color, pulseOpacity)} />
            <circle cx={0} cy={0} r={0.25} fill={color} />
          </svg>
        </div>
      </Draggable>
    </div>
  );
}

export default memo(IntFigMovablePoint);
